import {
  Badge,
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import { useState } from "react";
import { Record, useNotify, useUpdate } from "react-admin";

export const normalizeNoteRowText = (value?: string) =>
  (value || "").toLocaleLowerCase().replace(/[^a-z0-9\u00c0-\u00ff]/g, "");

export const hasMatchingNoteRow = (record: Record, search: string) => {
  const normalizedSearch = normalizeNoteRowText(search);
  return normalizedSearch.length > 0 && (record.delivery_note_rows || []).some(
    (row: Record) => normalizeNoteRowText(row.description).includes(normalizedSearch)
  );
};

const managementChoices = [
  { value: "", label: "---------" },
  { value: "not_managed", label: "Non gestita" },
  { value: "managed", label: "Gestita" },
  { value: "partially_managed", label: "Gestita parziale" },
];

interface DeliveryNoteRowsButtonProps {
  record: Record;
  search: string;
}

export const DeliveryNoteRowsButton = ({ record, search }: DeliveryNoteRowsButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [, setRowsVersion] = useState(0);
  const [update] = useUpdate();
  const notify = useNotify();
  const matching = hasMatchingNoteRow(record, search);
  const rows: Record[] = record.delivery_note_rows || [];
  const managedRows = rows.filter((row) => row.management_status === "managed").length;
  const allRowsManaged = managedRows === rows.length;

  if (rows.length === 0) return null;

  const updateManagementStatus = (row: Record, management_status: string) => {
    update(
      {
        resource: "delivery-note-rows",
        payload: { id: row.id, data: { management_status } },
      },
      {
        onSuccess: () => {
          row.management_status = management_status;
          setRowsVersion((version) => version + 1);
          notify("Stato riga aggiornato", "success");
        },
        onFailure: () => notify("Impossibile aggiornare lo stato della riga", "error"),
      }
    );
  };

  return (
    <>
      <Tooltip title={`Righe DDT: ${managedRows}/${rows.length} gestite`}>
        <IconButton
          size="small"
          style={{ color: allRowsManaged ? "#388e3c" : matching ? "#f57c00" : undefined }}
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
          }}
        >
          <Badge color={allRowsManaged ? "primary" : "secondary"} badgeContent={`${managedRows}/${rows.length}`}>
            {allRowsManaged ? <CheckCircleIcon fontSize="small" /> : managedRows > 0 ? (
              <IndeterminateCheckBoxIcon fontSize="small" />
            ) : <FormatListBulletedIcon fontSize="small" />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box width="calc(100vw - 24px)" maxWidth={680}>
          <Box px={1.5} py={1} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Articoli DDT</Typography>
            <Typography variant="caption" color={allRowsManaged ? "primary" : "textSecondary"}>
              {managedRows}/{rows.length} gestite
            </Typography>
          </Box>
          <TableContainer style={{ overflowX: "hidden" }}>
            <Table size="small" aria-label="Righe DDT" style={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell style={{ width: "43%", padding: "6px 12px", fontWeight: 600 }}>Articolo</TableCell>
                  <TableCell align="right" style={{ width: "12%", padding: "6px 8px", fontWeight: 600 }}>Qtà</TableCell>
                  <TableCell style={{ width: "45%", padding: "6px 12px", fontWeight: 600 }}>Gestione</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const rowMatches = hasMatchingNoteRow({ id: row.id, delivery_note_rows: [row] }, search);
                  return (
                    <TableRow key={row.id} style={{ backgroundColor: rowMatches ? "#fff3cd" : undefined }}>
                      <TableCell style={{ padding: "5px 12px", maxWidth: 180 }}>
                        <Typography variant="body2" noWrap title={row.description || "(senza descrizione)"}>
                          {row.description || "(senza descrizione)"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" style={{ padding: "5px 8px", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {row.quantity}
                      </TableCell>
                      <TableCell style={{ padding: "3px 12px" }}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={row.management_status || ""}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => updateManagementStatus(row, event.target.value as string)}
                          >
                            {managementChoices.map((choice) => (
                              <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box px={1} py={0.5} display="flex" justifyContent="flex-end">
            <Button size="small" onClick={() => setAnchorEl(null)}>Chiudi</Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
